import React, { useState, useEffect } from 'react';
import { Save, FolderOpen, Trash2, X, Clock, DollarSign, Star } from 'lucide-react';
import { getSlotMetas, getAutoSaveMeta, SlotMeta } from '../hooks/useSaveLoad';

interface SaveLoadModalProps {
  onClose: () => void;
  onSave: (slotIndex: number, name: string) => void;
  onLoad: (slotIndex: number) => void;
  onDelete: (slotIndex: number) => void;
  money: number;
  gameTime: number;
  reputation: number;
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatGameTime(ticks: number): string {
  const days = Math.floor(ticks / 1440);
  const hours = Math.floor((ticks % 1440) / 60);
  const mins = ticks % 60;
  if (days > 0) return `Day ${days}, ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

const SaveLoadModal: React.FC<SaveLoadModalProps> = ({
  onClose, onSave, onLoad, onDelete,
  money, gameTime, reputation,
}) => {
  const [slots, setSlots] = useState<(SlotMeta | null)[]>([null, null, null]);
  const [autoSave, setAutoSave] = useState<SlotMeta | null>(null);
  const [savingSlot, setSavingSlot] = useState<number | null>(null);
  const [saveName, setSaveName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  useEffect(() => {
    setSlots(getSlotMetas());
    setAutoSave(getAutoSaveMeta());
  }, []);

  const handleSaveClick = (i: number) => {
    const defaultName = slots[i]?.name || `Save ${i + 1}`;
    setSaveName(defaultName);
    setSavingSlot(i);
    setConfirmDelete(null);
  };

  const handleConfirmSave = () => {
    if (savingSlot === null) return;
    onSave(savingSlot, saveName);
    setSlots(getSlotMetas());
    setSavingSlot(null);
  };

  const handleLoad = (slotIndex: number) => {
    onLoad(slotIndex);
    onClose();
  };

  const handleDeleteClick = (i: number) => {
    if (confirmDelete === i) {
      onDelete(i);
      setSlots(getSlotMetas());
      setConfirmDelete(null);
    } else {
      setConfirmDelete(i);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  const renderSlot = (meta: SlotMeta | null, i: number) => {
    const isSaving = savingSlot === i;

    return (
      <div
        key={i}
        className={`rounded-lg border p-4 transition-colors ${
          meta ? 'bg-gray-800 border-gray-600' : 'bg-gray-800/50 border-gray-700 border-dashed'
        }`}
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Slot {i + 1}</span>
            {meta && (
              <span className="text-sm font-semibold text-white truncate max-w-[160px]">{meta.name}</span>
            )}
            {!meta && <span className="text-sm text-gray-500 italic">Empty</span>}
          </div>
        </div>

        {/* Save data summary */}
        {meta && !isSaving && (
          <div className="text-xs text-gray-400 space-y-1 mb-3">
            <div className="flex items-center gap-1.5">
              <Clock size={11} className="text-gray-500 shrink-0" />
              <span>{formatDate(meta.timestamp)}</span>
            </div>
            <div className="flex gap-4">
              <span className="flex items-center gap-1">
                <DollarSign size={11} className="text-green-400" />
                <span className="text-green-400">${meta.money.toLocaleString()}</span>
              </span>
              <span className="flex items-center gap-1">
                <Star size={11} className="text-yellow-400" />
                <span className="text-yellow-400">Rep {meta.reputation}</span>
              </span>
              <span className="text-gray-500">{formatGameTime(meta.gameTime)}</span>
            </div>
          </div>
        )}

        {/* Save name input */}
        {isSaving && (
          <div className="mb-3">
            <input
              autoFocus
              type="text"
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleConfirmSave(); if (e.key === 'Escape') setSavingSlot(null); }}
              maxLength={32}
              placeholder="Enter save name…"
              className="w-full bg-gray-700 border border-blue-500 rounded px-2 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <p className="text-xs text-gray-500 mt-1">Press Enter to confirm, Esc to cancel</p>
          </div>
        )}

        {/* Current game summary shown while naming */}
        {isSaving && (
          <div className="text-xs text-gray-400 flex gap-4 mb-3">
            <span className="flex items-center gap-1">
              <DollarSign size={11} className="text-green-400" />
              <span className="text-green-400">${money.toLocaleString()}</span>
            </span>
            <span className="flex items-center gap-1">
              <Star size={11} className="text-yellow-400" />
              <span className="text-yellow-400">Rep {reputation}</span>
            </span>
            <span className="text-gray-500">{formatGameTime(gameTime)}</span>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          {isSaving ? (
            <>
              <button
                onClick={handleConfirmSave}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-500 rounded text-sm font-semibold"
              >
                <Save size={14} />
                Save
              </button>
              <button
                onClick={() => setSavingSlot(null)}
                className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 rounded text-sm"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => handleSaveClick(i)}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-700 hover:bg-blue-600 rounded text-sm font-semibold"
                title={meta ? 'Overwrite this slot' : 'Save to this slot'}
              >
                <Save size={14} />
                {meta ? 'Overwrite' : 'Save here'}
              </button>
              {meta && (
                <>
                  <button
                    onClick={() => handleLoad(i)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-700 hover:bg-yellow-600 rounded text-sm font-semibold"
                    title="Load this save"
                  >
                    <FolderOpen size={14} />
                    Load
                  </button>
                  <button
                    onClick={() => handleDeleteClick(i)}
                    className={`px-2.5 py-1.5 rounded text-sm font-semibold ${
                      confirmDelete === i
                        ? 'bg-red-600 hover:bg-red-500 animate-pulse'
                        : 'bg-gray-700 hover:bg-red-800'
                    }`}
                    title={confirmDelete === i ? 'Click again to confirm delete' : 'Delete save'}
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Save size={18} className="text-blue-400" />
            Save / Load Game
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {/* 3 manual slots */}
          {slots.map((meta, i) => renderSlot(meta, i))}

          {/* Auto-save slot (load-only) */}
          {autoSave && (
            <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-4">
              <div className="flex items-start justify-between mb-2">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Auto-Save</span>
              </div>
              <div className="text-xs text-gray-400 flex gap-4 mb-3">
                <span className="flex items-center gap-1">
                  <Clock size={11} className="text-gray-500" />
                  <span>{formatDate(autoSave.timestamp)}</span>
                </span>
                <span className="flex items-center gap-1">
                  <DollarSign size={11} className="text-green-400" />
                  <span className="text-green-400">${autoSave.money.toLocaleString()}</span>
                </span>
              </div>
              <button
                onClick={() => handleLoad(-1)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-700 hover:bg-yellow-600 rounded text-sm font-semibold"
              >
                <FolderOpen size={14} />
                Load Auto-Save
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaveLoadModal;
